import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Rating,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmptyStart from 'components/svg/emptyStar.svg';
import FullStart from 'components/svg/fullStar.svg';
import Button from 'components/common/Button';
import { makeStyles } from 'tss-react/mui';
import { useDispatch } from 'react-redux';
import { makeEntityLoader } from 'hooks/makeSimpleLoader';
import { EventsActions, SurveysActions } from 'store/actions';
import { EventsSelectors } from 'store/selectors';
import { useHashParams } from 'hooks';
import { useTranslation } from 'i18n';
import EventInfo from './EventInfo';

interface FeedbackModalProps extends DialogProps {
  userId?: number;
  surveyId: string;
  eventId: number;
  rate: number;
  setSuccess?: () => void;
}

const useEventState = makeEntityLoader(EventsActions.getEvent, EventsSelectors.eventStateById);
const useStyles = makeStyles()((theme) => ({
  radio: {
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
    '&.Mui-checked': {
      color: '#5570FD',
    },
  },
  singleOption: {
    display: 'flex',
    letterSpacing: '0.00em',
    color: '#000000',
    height: 56,
    width: 239,
    textTransform: 'none',
    padding: '11px 13px',
    maxWidth: '250px',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#A1ACC4',
    borderRadius: 8,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(209, 223, 255, 0.30)',
      borderColor: '#9BB3FA',
    },
    '&.selected': {
      backgroundColor: 'rgba(209, 223, 255, 0.30)',
      borderColor: '#9BB3FA',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '300px',
    },
  },
  optionsWrapper: {
    display: 'flex',
  },
  optionWrapper: {
    flex: 1,
  },
  textField: {
    '& .MuiInputBase-input': {
      height: 98,
      [theme.breakpoints.down('md')]: {
        height: '143px !important',
      },
    },
  },
}));

const FeedbackModal = (props: FeedbackModalProps) => {
  const { eventId, rate, onClose, surveyId, userId, setSuccess, ...other } = props;
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(rate || null);
  const [hoverRating, setHoverRating] = useState<number>(-1);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);
  const [connection, setConnection] = useState('');
  const [isSkip, setIsSkip] = useState(false);
  const eventState = useEventState(() => [eventId], [eventId]);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [error, setError] = useState(false);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [, setHashParams] = useHashParams();
  const [t] = useTranslation('public');

  const labels = useMemo(
    () => ({
      0: t('feedbackModal.rate.selectRating'),
      1: t('feedbackModal.rate.notEnjoyEvent'),
      2: t('feedbackModal.rate.dislikedPartsOfEvent'),
      3: t('feedbackModal.rate.eventWasOkay'),
      4: t('feedbackModal.rate.iLikedEvent'),
      5: t('feedbackModal.rate.iLovedEvent'),
    }),
    [t],
  );

  const options = useMemo(
    () => [
      { label: t('feedbackModal.answers.stronglyAgree') },
      { label: t('feedbackModal.answers.agree') },
      { label: t('feedbackModal.answers.neutral') },
      { label: t('feedbackModal.answers.disagree') },
      { label: t('feedbackModal.answers.stronglyDisagree') },
    ],
    [t],
  );

  const renderLabel = () => {
    if (hoverRating !== -1) {
      return (
        <Typography variant="subtitle2" sx={{ mt: '23px', mb: '16px' }}>
          {labels[hoverRating as keyof typeof labels]}
        </Typography>
      );
    }

    if (rating !== null) {
      return (
        <Typography variant="subtitle2" sx={{ mt: '23px', mb: '16px' }}>
          {labels[rating as keyof typeof labels]}
        </Typography>
      );
    }

    return (
      <Typography variant="subtitle2" color={error ? 'error' : 'textPrimary'} sx={{ mt: '23px', mb: '16px' }}>
        {labels[0]}
      </Typography>
    );
  };

  const handleNext = useCallback(() => setStep(2), [setStep]);

  const handleSkip = useCallback(() => {
    setIsSkip(true);
    if (userId) {
      dispatch(
        SurveysActions.updateSurvey.request({
          id: surveyId,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          params: {
            userId,
          },
          entity: { is_skipped: true },
        }),
      );
    } else
      dispatch(
        SurveysActions.updateSurveyByEmail.request({
          id: surveyId,
          params: {
            token: surveyId,
          },
          entity: { is_skipped: true },
        }),
      );
    handleNext();
  }, [surveyId, dispatch, handleNext, setIsSkip, userId]);

  const handleClose = useCallback(() => {
    if (step === 1) handleSkip();
    setStep(1);
    setRating(null);
    setComment('');
    setConnection('');
    onClose({}, null);
  }, [onClose, setStep, step, handleSkip]);

  const handleSubmit = useCallback(() => {
    if (rating && !commentError) {
      if (userId) {
        dispatch(
          SurveysActions.updateSurvey.request({
            id: surveyId,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            params: {
              userId,
            },
            // TODO:
            entity: { rate: rating, is_answered: true, ...(comment && { comment }) },
          }),
        );
        setSuccess();
      } else
        dispatch(
          SurveysActions.updateSurveyByEmail.request({
            id: surveyId,
            params: {
              token: surveyId,
            },
            // TODO:
            entity: { rate: rating, is_answered: true, ...(comment && { comment }) },
          }),
        );
      handleNext();
    } else setError(true);
  }, [rating, commentError, userId, dispatch, surveyId, comment, handleNext, setSuccess]);

  const handleDone = useCallback(() => {
    if (connection) {
      if (userId) {
        dispatch(
          SurveysActions.updateSurvey.request({
            id: surveyId,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            params: {
              userId,
            },
            entity: { answers: [{ question: t('feedbackModal.title2'), answer: connection }] },
          }),
        );
      } else
        dispatch(
          SurveysActions.updateSurveyByEmail.request({
            id: surveyId,
            params: {
              token: surveyId,
            },
            entity: { answers: [{ question: t('feedbackModal.title2'), answer: connection }] },
          }),
        );
    }

    if (!isSkip && !userId) setHashParams({ surveySuccess: true });
    handleClose();
  }, [connection, isSkip, userId, setHashParams, handleClose, dispatch, surveyId, t]);

  return (
    <Dialog
      PaperProps={
        isMobile
          ? {
              style: {
                minWidth: '100%',
                minHeight: '100%',
              },
            }
          : {
              style: {
                minWidth: '622px',
                minHeight: '618px',
              },
            }
      }
      onClose={(_event, reason) => {
        // Prevent closing when clicking the backdrop
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      {...other}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          ...(isMobile ? { display: 'block' } : { paddingBottom: 0 }),
        }}
      >
        {isMobile ? (
          <>
            <IconButton onClick={handleClose} size="small" sx={{ alignItems: 'start', height: '34px', width: '34px' }}>
              <CloseIcon />
            </IconButton>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{
                position: 'absolute',
                top: '21px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {step === 1 ? t('feedbackModal.question1Of2') : t('feedbackModal.question2Of2')}
            </Typography>
            <Typography
              // variant="h5"
              fontSize="24px"
              fontWeight={700}
              lineHeight="24px"
              sx={step === 1 ? { padding: '56px 6px 0px 11px' } : { padding: '56px 11px 26px 11px' }}
            >
              {step === 1 ? t('feedbackModal.title1') : t('feedbackModal.title2')}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              // variant="h5"
              lineHeight="24px"
              fontSize="24px"
              fontWeight={700}
              sx={step === 1 ? { padding: '27px 47px 16px 51px' } : { padding: '80px 17px 35px 47px' }}
            >
              {step === 1 ? t('feedbackModal.title1') : t('feedbackModal.title2')}
            </Typography>
            <IconButton onClick={handleClose} size="small" sx={{ alignItems: 'start', height: '34px' }}>
              <CloseIcon />
            </IconButton>
          </>
        )}
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '0 !important', ...(isMobile && { padding: '0px 24px 0px 24px' }) }}>
        {step === 1 && (
          <>
            {eventState.entity && (
              <Box sx={isMobile ? { padding: '0px 0px 59px 0px' } : { padding: '0px 32px 59px 32px' }}>
                <EventInfo event={eventState.entity} />
              </Box>
            )}
            <Box textAlign="center">
              <Rating
                id={surveyId}
                sx={{
                  '& .MuiRating-icon': {
                    marginRight: '12px', // adjust spacing here
                  },
                }}
                value={rating}
                onChange={(e, newValue) => setRating(newValue)}
                onChangeActive={(e, newHover) => {
                  setHoverRating(newHover);
                }}
                size="large"
                icon={<FullStart fontSize="inherit" />}
                emptyIcon={<EmptyStart fontSize="inherit" />}
              />
              {renderLabel()}
              <Box sx={{ padding: isMobile ? '61px 0px 0px 0px' : '0px 28px 0px 28px' }}>
                <TextField
                  className={classes.textField}
                  fullWidth
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                  label={t('common:fields.label.yourComment')}
                  placeholder={t('common:fields.placeholder.addCommentOptional')}
                  value={comment}
                  error={commentError}
                  helperText={commentError ? 'Comment cannot exceed 500 characters' : ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value.length > 500) {
                      setCommentError(true);
                    } else {
                      setCommentError(false);
                    }
                    setComment(value);
                  }}
                />
                <Typography fontSize={13} fontWeight={400} sx={{ mt: '8px', textAlign: 'left' }}>
                  {t('feedbackModal.comment.description')}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {step === 2 && (
          <Box textAlign="center" sx={{ padding: isMobile ? '16px' : '0px 32px 0px 32px' }}>
            <Grid container spacing={1}>
              {options.map((opt) => (
                <Grid item xs={12} style={{ display: 'flex' }} justifyContent="center" alignItems="center">
                  <Button
                    className={`${classes.singleOption} ${opt.label === connection ? 'selected' : ''}`}
                    // sx={opt.label === connection ? { borderColor: '#5570FD' } : { borderColor: '#A1ACC4' }}
                    key={opt.label}
                    color="primary"
                    variant="outlined"
                    onClick={() => setConnection(opt.label)}
                    fullWidth
                  >
                    {opt.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: isMobile ? '16px 24px' : '19px 64px 40px 64px' }}>
        {step === 1 && (
          <Grid container alignItems="center" justifyContent="space-between">
            {!isMobile && (
              <Grid item>
                <Typography variant="body2" fontWeight={500} color="textSecondary">
                  {t('feedbackModal.question1Of2')}
                </Typography>
              </Grid>
            )}
            <Grid item {...(isMobile && { xs: 12 })}>
              <Grid container spacing={1} {...(isMobile && { alignItems: 'center', justifyContent: 'space-between' })}>
                <Grid item {...(isMobile && { xs: 6 })}>
                  <Button style={{ height: 40 }} variant="outlined" rounded onClick={handleSkip} fullWidth={isMobile}>
                    {t<string>('common:button.skip')}
                  </Button>
                </Grid>
                <Grid item {...(isMobile && { xs: 6 })}>
                  <Button
                    style={{ height: 40 }}
                    rounded
                    variant="contained"
                    onClick={handleSubmit}
                    fullWidth={isMobile}
                  >
                    {t<string>('common:button.next')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {step === 2 && (
          <Grid container alignItems="center" justifyContent="space-between">
            {!isMobile && (
              <Grid item>
                <Typography variant="body2" fontWeight={500} color="textSecondary">
                  {t('feedbackModal.question2Of2')}
                </Typography>
              </Grid>
            )}
            <Grid item {...(isMobile && { xs: 12 })}>
              <Grid container spacing={1}>
                <Grid item {...(isMobile && { xs: 12 })}>
                  <Button style={{ height: 40 }} rounded variant="contained" onClick={handleDone} fullWidth={isMobile}>
                    {t<string>('common:button.done')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
